// Overview component for Leave Requests (Ledighetsansökningar)
// Provides: Group filter dropdown and status filter, displays forms data from API
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Box, Typography, Paper, Button, IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getMyMentorGroups, getSchoolMentorGroups } from 'actions/groups';
import { getLeaveRequests as getLeaveRequestsAction } from 'actions/leaveRequests';
import { connect } from 'react-redux';
import { setPageTitle, setPageActions } from 'actions/header';
import { translate, getActiveLanguage } from '@haldor/ui';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import Moment from 'moment';
import { getNormalizedActiveLanguage, getPickersLocale } from 'helpers/locale';
import SelectMultiple from 'containers/Overview/TableFilterComponents/SelectMultiple';
import { Spinner } from 'UI';
import Modal from 'containers/Modals/Modal';
import User from '_class/User';
import LeaveRequestForm from './LeaveRequestForm';
import LeaveRequest from '_class/LeaveRequest';
import LeaveRequestsTable from './LeaveRequestsTable';

/**
 * Overview (Ledighetsansökningar)
 * Props:
 *  - defaultGroupId: id to preselect
 *  - onGroupChange(groupIds: array)
 */

// MUI X v7 wraps the picker button in a Tooltip, which causes a prop-type warning
// with React 17 (MUI X v7 requires React 18). Override the slot with a plain
// IconButton so no Tooltip is rendered, avoiding the warning without any UX change.
const NoTooltipPickerButton = React.forwardRef(function NoTooltipPickerButton({ ownerState, ...props }, ref) {
	return <IconButton ref={ref} {...props} />;
});

const LeaveRequestsOverview = ({
	defaultGroupId = null,
	onGroupChange = (ids) => { },
	getMyMentorGroups,
	setPageActions,
	setPageTitle,
	leaveRequests,
	translate,
	studentId = null,
	externalStartDate = null,
	externalEndDate = null,
	disablePageTitle = false,
}) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const [mentorGroups, setMentorGroups] = useState(null);
	const [studentModalOpen, setStudentModalOpen] = useState(false);
	// get current user from redux and wrap with User helper
	const currentUser = useSelector(state => state.user.currentUser);
	const userObj = useMemo(() => (currentUser ? new User(currentUser) : null), [currentUser]);
	const [loadingGroups, setLoadingGroups] = useState(true);
	const [loadingRequests, setLoadingRequests] = useState(false);
	const [selectedGroupIds, setSelectedGroupIds] = useState(defaultGroupId != null ? [defaultGroupId] : []);

	// Status filter (default: empty to show all statuses)
	const STATUS_OPTIONS = useMemo(() => ([
		{ id: 'PENDING', title: translate('Pending decision') },
		{ id: 'APPROVED', title: translate('Approved') },
		{ id: 'REJECTED', title: translate('Rejected') },
		{ id: 'REVOKED', title: translate('Withdrawn') },
	]), []);
	const hasExternalFilters = externalStartDate !== null || externalEndDate !== null;
	const [selectedStatuses, setSelectedStatuses] = useState([]);

	// Date filters
	// Default: start = one month before now, end = now + 1 day
	const defaultStartDate = externalStartDate ? Moment(externalStartDate) : Moment().subtract(1, 'month');
	const defaultEndDate = externalEndDate ? Moment(externalEndDate) : Moment().add(1, 'day');

	const [startDate, setStartDate] = useState(defaultStartDate);
	const [endDate, setEndDate] = useState(defaultEndDate);


	const lastFilterRef = React.useRef(null);

	const fetchLeaveRequests = React.useCallback((force = false) => {
		if (!mentorGroups) return Promise.resolve();


		// Build filter string without leading/trailing commas
		const filterParts = [];
		if (studentId) {
			filterParts.push(`studentId:${studentId}`);
		}

		const effectiveStartDate = externalStartDate || startDate;
		const effectiveEndDate = externalEndDate || endDate;
		if (effectiveStartDate) {
			const formattedStartDate = Moment(effectiveStartDate).format('YYYY-MM-DD');
			filterParts.push(`createdafter:${formattedStartDate}`);
		}
		if (effectiveEndDate) {
			const formattedEndDate = Moment(effectiveEndDate).format('YYYY-MM-DD');
			filterParts.push(`createdbefore:${formattedEndDate}`);
		}

		const filter = filterParts.join(',');

		if (!force && lastFilterRef.current === filter) {
			return Promise.resolve();
		}
		lastFilterRef.current = filter;

		setLoadingRequests(true);
		const promise = dispatch(getLeaveRequestsAction({ filter })).finally(() => {
			setLoadingRequests(false);
		});

		return promise;
	}, [mentorGroups, dispatch, startDate, endDate, studentId, externalStartDate, externalEndDate]);

	// Fetch mentor groups on mount
	useEffect(() => {
		let active = true;
		if (userObj == null) { return };

		(async () => {
			try {
				setLoadingGroups(true);

				let result = [];
				if (userObj && (userObj.isLeaveAdministrator() || userObj.isAdministrator())) {
					const activeSchoolId = userObj.getActiveSchool()?.id;
					result = await dispatch(getSchoolMentorGroups(activeSchoolId));
					if (!active) return;
				} else {
					result = await dispatch(getMyMentorGroups());
					if (!active) return;	
				}
				// normalize to array
				setMentorGroups(Array.isArray(result) ? result : []);
			} catch (error) {
				if (active) setMentorGroups([]);
			} finally {
				if (active) setLoadingGroups(false);
			}
		})();
		return () => { active = false; };
	}, [getMyMentorGroups, userObj]); // mount

	// set up page actions for student users using setPageActions if available
	useEffect(() => {

		if (userObj && userObj.isStudent() && userObj.isAuthoritative()) {
			const actions = [
				{
					type: 'button',
					value: translate('Apply for leave'),
					onClick: () => setStudentModalOpen(true),
					buttonType: 'primary',
					icon: 'plus'
				}
			];

			setPageActions(actions);
			return () => {
				// clear actions on unmount
				setPageActions([]);
			};
		}
		// if no student actions, ensure actions cleared on unmount
		return () => { if (typeof setPageActions === 'function') setPageActions([]); };
	}, [userObj, setPageActions]);

	// set page title for this overview
	useEffect(() => {
		if (!disablePageTitle) {
			setPageTitle(translate('Leave applications'));
		}
	}, [setPageTitle, disablePageTitle]);

	// Fetch data when filters or callback change
	useEffect(() => {
		if (mentorGroups) {
			fetchLeaveRequests();
		}
	}, [mentorGroups, fetchLeaveRequests]);

	const handleGroupsChange = useCallback((selectedObjs) => {
		const ids = selectedObjs.map(o => o.id);
		setSelectedGroupIds(ids);
		onGroupChange && onGroupChange(ids);
	}, [onGroupChange]);

	const handleStatusesChange = useCallback((selectedObjs) => {
		const ids = selectedObjs.map(o => o.id);
		setSelectedStatuses(ids);
	}, []);

	const clearAllFilters = useCallback(() => {
		setSelectedGroupIds([]);
		setSelectedStatuses([]);

		const resetStart = externalStartDate ? Moment(externalStartDate) : Moment().subtract(1, 'month');
		const resetEnd = externalEndDate ? Moment(externalEndDate) : Moment().add(1, 'day');

		setStartDate(resetStart);
		setEndDate(resetEnd);
	}, [externalStartDate, externalEndDate]);

	const datesAreDefault = (startDate && endDate)
		? (Moment(startDate).isSame(defaultStartDate, 'day') && Moment(endDate).isSame(defaultEndDate, 'day'))
		: false;

	const hasActiveFilters = selectedGroupIds.length > 0 || selectedStatuses.length > 0 || !datesAreDefault;

	const groupOptions = useMemo(() => (mentorGroups || []).map(g => ({ id: g.id, title: g.name || g.title || g.groupName || `Grupp ${g.id}` })), [mentorGroups]);
	const initialSelectedItems = useMemo(() => groupOptions.filter(g => selectedGroupIds.includes(g.id)), [groupOptions, selectedGroupIds]);
	const initialSelectedStatusItems = useMemo(() => STATUS_OPTIONS.filter(s => selectedStatuses.includes(s.id)), [STATUS_OPTIONS, selectedStatuses]);

	// Filter rows by leave requests (base data)
	const filteredRows = useMemo(() => {
		let result = LeaveRequest.toTableRows(leaveRequests);
		if (selectedStatuses && selectedStatuses.length > 0) {
			result = result.filter(r => r.normalizedStatus != null && selectedStatuses.includes(r.normalizedStatus.toUpperCase()));
		}

		return result;
	}, [leaveRequests, selectedStatuses]);

	return (
		<>
			<Box sx={{ width: '100%', p: { xs: 2, md: 0 } }}>
				<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 4, mb: 2, flexWrap: 'wrap' }}>
					{!hasExternalFilters && (
						<>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
								<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{translate('start')}</Typography>
								<LocalizationProvider
									dateAdapter={AdapterMoment}
									adapterLocale={getNormalizedActiveLanguage()}
									localeText={(() => {
										const pl = getPickersLocale(getNormalizedActiveLanguage());
										return pl?.components?.MuiLocalizationProvider?.defaultProps?.localeText || undefined;
									})()}
								>
									<DatePicker
										value={startDate}
										onChange={(date) => setStartDate(date)}
									slots={{ openPickerButton: NoTooltipPickerButton }}
									slotProps={{
										textField: {
											fullWidth: false,
											sx: {
												backgroundColor: '#FFFFFF',
												width: 170,
												'& .MuiInputBase-root': {
													height: '40px'
												}
											}
										}
										}}
									/>
								</LocalizationProvider>
							</Box>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
								<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{translate('end')}</Typography>
								<LocalizationProvider
									dateAdapter={AdapterMoment}
									adapterLocale={getNormalizedActiveLanguage()}
									localeText={(() => {
										const pl = getPickersLocale(getNormalizedActiveLanguage());
										return pl?.components?.MuiLocalizationProvider?.defaultProps?.localeText || undefined;
									})()}
								>
									<DatePicker
										value={endDate}
										onChange={(date) => setEndDate(date)}
										minDate={startDate || undefined}
									slots={{ openPickerButton: NoTooltipPickerButton }}
									slotProps={{
										textField: {
											fullWidth: false,
											sx: {
												backgroundColor: '#FFFFFF',
												width: 180,
												'& .MuiInputBase-root': {
													height: '40px'
												}
											}
										}
										}}
									/>
								</LocalizationProvider>
							</Box>
						</>
					)}
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
						<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{translate('Status')}</Typography>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<SelectMultiple
								label={selectedStatuses.length > 0 ? `${selectedStatuses.length} / ${STATUS_OPTIONS.length}` : translate('Select status')}
								options={STATUS_OPTIONS}
								initialSelectedItems={initialSelectedStatusItems}
								onChange={handleStatusesChange}
								sx={{
									backgroundColor: '#FFFFFF',
									minWidth: 220,
									'& .MuiInputBase-root': {
										height: '40px',
										minHeight: '40px',
										maxHeight: '40px'
									}
								}}
							/>

							{hasActiveFilters && (
								<Button
									variant="outlined"
									startIcon={<ClearIcon />}
									onClick={clearAllFilters}
									sx={{ height: '40px', whiteSpace: 'nowrap' }}
								>
									{translate('Clear filter')}
								</Button>
							)}
						</Box>
					</Box>
					{!(userObj && userObj.isStudent()) && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
							<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{translate('Mentor groups')}</Typography>
							<SelectMultiple
								label={loadingGroups ? translate('Loading...') : (selectedGroupIds.length > 0 ? `${selectedGroupIds.length} / ${groupOptions.length}` : translate('Select group'))}
								options={groupOptions}
								initialSelectedItems={initialSelectedItems}
								onChange={handleGroupsChange}
								disable={loadingGroups || groupOptions.length === 0}
								sx={{
									backgroundColor: '#FFFFFF',
									minWidth: 220,
									'& .MuiInputBase-root': {
										height: '40px',
										minHeight: '40px',
										maxHeight: '40px'
									}
								}}
							/>
						</Box>
					)}
					{/* Clear filter button now shown alongside the Status control */}
				</Box>

				{/* Info text above filters */}
				<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
					<InfoOutlinedIcon sx={{ color: '#b0b0b0', mr: 1 }} />
					<Typography variant="body2" sx={{ color: '#b0b0b0' }}>
						{translate('Selected date range refers to when the application was submitted, not the requested leave period.')}
					</Typography>
				</Box>

				<Paper elevation={1} sx={{ width: '100%' }}>
					<Box sx={{ p: 0 }}>
						{loadingGroups || loadingRequests ? (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
								<Spinner center />
							</Box>
						) : (
							<LeaveRequestsTable
								rows={filteredRows}
								loading={false}
								selectedGroupIds={selectedGroupIds}
								onRowClick={(row) => history.push({
									pathname: `/leaves/${row.id}`,
									state: { leaveRequest: row }
								})}
							/>
						)}
					</Box>
				</Paper>

				{/* Student modal for creating a new leave request (simple placeholder) */}
				<Modal type="leave-request" isOpen={studentModalOpen} onClose={() => setStudentModalOpen(false)} title={translate('Create leave application')}>
					<Box sx={{ p: 2 }}>
						<LeaveRequestForm
							onAbort={() => setStudentModalOpen(false)}
							onSuccess={() => { setStudentModalOpen(false); fetchLeaveRequests(true); }}
						/>
					</Box>
				</Modal>
			</Box>
		</>
	);
};

export default connect(
	(state) => ({
		leaveRequests: state?.LeaveRequests.leaveRequests,
		translate: translate(state.Languages.translations),
	}),
	{ getMyMentorGroups, getSchoolMentorGroups, setPageActions, setPageTitle }
)(LeaveRequestsOverview);

